/**
 * Free Gift with Purchase — Shopify Theme Script
 * ------------------------------------------------
 * Awards 1 free product per $50 spent in the cart.
 * The free product has size variants the customer can choose from.
 *
 * CONFIGURATION: Set the constants below before deploying.
 */

(function () {
  'use strict';

  // ======================== CONFIGURATION ========================
  // Reads from window.__freeGiftConfig injected by the Liquid snippet
  const injected = window.__freeGiftConfig || {};

  const CONFIG = {
    threshold: injected.threshold || 50,
    freeProductId: injected.productId || null,
    giftPropertyKey: '_free_gift',
    giftPropertyValue: 'true',
  };

  // ======================== STATE ========================
  let freeGiftVariants = [];
  let isProcessing = false;
  let currentCart = null;

  // ======================== HELPERS ========================

  /**
   * Fetch the current cart as JSON.
   */
  async function fetchCart() {
    const res = await fetch('/cart.json');
    if (!res.ok) throw new Error('Failed to fetch cart');
    currentCart = await res.json();
    return currentCart;
  }

  /**
   * Fetch the free gift product data by product ID and populate variants.
   * Uses Shopify's /products.json endpoint and filters by ID.
   */
  async function fetchFreeGiftProduct() {
    if (!CONFIG.freeProductId) {
      throw new Error('No freeProductId configured');
    }

    // Paginate through /products.json to find the product by ID
    let page = 1;
    const limit = 250;

    while (true) {
      const res = await fetch(`/products.json?limit=${limit}&page=${page}`);
      if (!res.ok) throw new Error('Failed to fetch products');

      const data = await res.json();
      const products = data.products || [];

      const match = products.find((p) => p.id === CONFIG.freeProductId);

      if (match) {
        freeGiftVariants = match.variants.map((v) => ({
          id: v.id,
          title: v.title,
          available: v.available,
          price: v.price,
        }));
        // Cache the handle for potential future use
        CONFIG.freeProductHandle = match.handle;
        return match;
      }

      // No more pages to check
      if (products.length < limit) {
        throw new Error(`Product with ID ${CONFIG.freeProductId} not found`);
      }

      page++;
    }
  }

  /**
   * Calculate the cart total EXCLUDING any free gift line items (in dollars).
   */
  function getQualifyingTotal(cart) {
    let total = 0;
    for (const item of cart.items) {
      if (isFreeGiftItem(item)) continue;
      total += item.final_line_price; // in cents
    }
    return total / 100;
  }

  /**
   * Count how many free gift items are currently in the cart.
   */
  function countFreeGiftsInCart(cart) {
    let count = 0;
    for (const item of cart.items) {
      if (isFreeGiftItem(item)) {
        count += item.quantity;
      }
    }
    return count;
  }

  /**
   * Check if a line item is a free gift.
   */
  function isFreeGiftItem(item) {
    if (!item.properties) return false;
    return item.properties[CONFIG.giftPropertyKey] === CONFIG.giftPropertyValue;
  }

  /**
   * Get the number of free gifts the customer is entitled to.
   */
  function getAllowedGiftCount(cart) {
    const total = getQualifyingTotal(cart);
    return Math.floor(total / CONFIG.threshold);
  }

  /**
   * Add a free gift variant to the cart.
   */
  async function addFreeGift(variantId) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: {
              [CONFIG.giftPropertyKey]: CONFIG.giftPropertyValue,
            },
          },
        ],
      }),
    });
    if (!res.ok) throw new Error('Failed to add free gift');
    return res.json();
  }

  /**
   * Remove excess free gifts from the cart.
   * Removes the most recently added free gifts first.
   */
  async function removeExcessGifts(cart, allowed) {
    const giftItems = cart.items.filter((item) => isFreeGiftItem(item));
    let currentGiftCount = giftItems.reduce((sum, item) => sum + item.quantity, 0);
    const toRemove = currentGiftCount - allowed;

    if (toRemove <= 0) return;

    const updates = {};
    let removed = 0;

    // Iterate in reverse to remove most recently added first
    for (let i = giftItems.length - 1; i >= 0 && removed < toRemove; i--) {
      const item = giftItems[i];
      const key = item.key;
      const removeFromThis = Math.min(item.quantity, toRemove - removed);
      const newQty = item.quantity - removeFromThis;
      updates[key] = newQty;
      removed += removeFromThis;
    }

    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    });

    if (!res.ok) throw new Error('Failed to remove excess gifts');
    return res.json();
  }

  // ======================== UI ========================

  /**
   * Render the free gift status bar and size picker.
   */
  function renderUI(cart) {
    const container = document.getElementById('free-gift-app');
    if (!container) return;

    const qualifyingTotal = getQualifyingTotal(cart);
    const allowed = getAllowedGiftCount(cart);
    const current = countFreeGiftsInCart(cart);
    const canAddMore = allowed > current;
    const nextThreshold = (Math.floor(qualifyingTotal / CONFIG.threshold) + 1) * CONFIG.threshold;
    const amountToNext = nextThreshold - qualifyingTotal;

    // Nothing to show on an empty / zero-value cart
    if (qualifyingTotal <= 0 && current <= 0) {
      container.innerHTML = '';
      return;
    }

    let html = '';

    if (qualifyingTotal > 0 && qualifyingTotal < CONFIG.threshold && current === 0) {
      // ---- Not yet qualified — progress toward first gift ----
      const progress = (qualifyingTotal / CONFIG.threshold) * 100;
      html = `
        <div class="fg-bar">
          <div class="fg-bar__track">
            <div class="fg-bar__fill" style="width: ${progress}%"></div>
          </div>
          <div class="fg-bar__status">
            <span class="fg-bar__icon">🎁</span>
            <span>Add <strong>$${amountToNext.toFixed(2)}</strong> more for a <strong>FREE gift</strong>!</span>
          </div>
        </div>`;
    } else if (canAddMore) {
      // ---- Qualified — compact size picker ----
      const giftsAvailable = allowed - current;
      const nextHint = amountToNext > 0 && amountToNext < CONFIG.threshold
        ? ` <span class="fg-bar__hint">· $${amountToNext.toFixed(2)} to next</span>`
        : '';
      html = `
        <div class="fg-bar fg-bar--eligible">
          <div class="fg-bar__track">
            <div class="fg-bar__fill" style="width: 100%"></div>
          </div>
          <div class="fg-bar__status">
            <span class="fg-bar__icon">🎉</span>
            <span><strong>${giftsAvailable} free gift${giftsAvailable > 1 ? 's' : ''}</strong> unlocked!${nextHint}</span>
          </div>
          <div class="fg-picker">
            <select class="fg-picker__select" aria-label="Choose gift size">
              <option value="">Choose size</option>
              ${freeGiftVariants.map((v) => `
                <option value="${v.id}" ${!v.available ? 'disabled' : ''}>
                  ${v.title}${!v.available ? ' (Sold out)' : ''}
                </option>
              `).join('')}
            </select>
            <button class="fg-picker__add" disabled>Add</button>
          </div>
        </div>`;
    } else if (allowed > 0 && current >= allowed) {
      // ---- All gifts claimed — progress to next tier ----
      const progress = ((qualifyingTotal % CONFIG.threshold) / CONFIG.threshold) * 100;
      const nextMsg = amountToNext < CONFIG.threshold
        ? ` · <strong>$${amountToNext.toFixed(2)}</strong> to next!`
        : '';
      html = `
        <div class="fg-bar fg-bar--claimed">
          <div class="fg-bar__track">
            <div class="fg-bar__fill" style="width: ${progress}%"></div>
          </div>
          <div class="fg-bar__status">
            <span class="fg-bar__icon">✅</span>
            <span>${current} gift${current > 1 ? 's' : ''} added${nextMsg}</span>
          </div>
        </div>`;
    }

    container.innerHTML = html;

    // Bind size picker events
    const select = container.querySelector('.fg-picker__select');
    const addBtn = container.querySelector('.fg-picker__add');
    if (select && addBtn) {
      select.addEventListener('change', () => {
        addBtn.disabled = !select.value;
      });
      addBtn.addEventListener('click', () => {
        if (select.value) handleAddGift(parseInt(select.value, 10));
      });
    }
  }

  /**
   * Handle adding a free gift from the size picker.
   */
  async function handleAddGift(variantId) {
    if (isProcessing) return;
    isProcessing = true;

    const addBtn = document.querySelector('.fg-picker__add');
    if (addBtn) {
      addBtn.classList.add('fg-picker__add--loading');
      addBtn.textContent = '...';
    }

    try {
      await addFreeGift(variantId);
      const cart = await fetchCart();
      renderUI(cart);
      refreshCartUI();
    } catch (err) {
      console.error('[FreeGift] Failed to add gift:', err);
      if (addBtn) addBtn.textContent = 'Error';
      setTimeout(() => renderUI(currentCart), 1500);
    } finally {
      isProcessing = false;
    }
  }

  /**
   * Attempt to refresh the theme's native cart UI.
   * Different themes use different methods — we try several approaches.
   */
  function refreshCartUI() {
    // Method 1: Dispatch a custom event many themes listen for
    document.dispatchEvent(new CustomEvent('cart:refresh'));

    // Method 2: Shopify Section Rendering API (OS 2.0 themes)
    const cartSections = document.querySelectorAll('[data-section-id]');
    if (cartSections.length) {
      fetch(`${window.location.pathname}?sections=${Array.from(cartSections).map(s => s.dataset.sectionId).join(',')}`)
        .then(res => res.json())
        .then(sections => {
          Object.entries(sections).forEach(([id, html]) => {
            const target = document.querySelector(`[data-section-id="${id}"]`);
            if (target) {
              const temp = document.createElement('div');
              temp.innerHTML = html;
              const newContent = temp.querySelector(`[data-section-id="${id}"]`);
              if (newContent) target.innerHTML = newContent.innerHTML;
            }
          });
        })
        .catch(() => {});
    }

    // Method 3: Reload page if on cart page (fallback)
    // Uncomment if needed:
    // if (window.location.pathname === '/cart') window.location.reload();
  }

  // ======================== CART MONITORING ========================

  /**
   * Sync free gifts with cart state.
   * Called after any cart change to ensure the correct number of gifts.
   */
  async function syncGifts() {
    if (isProcessing) return;
    isProcessing = true;

    try {
      const cart = await fetchCart();
      const allowed = getAllowedGiftCount(cart);
      const current = countFreeGiftsInCart(cart);

      if (current > allowed) {
        // Instant visual feedback — mark gift line items before the API call
        document.querySelectorAll('.fg-line-item').forEach((el) => {
          el.classList.add('fg-line-item--removing');
        });

        const updatedCart = await removeExcessGifts(cart, allowed);
        if (updatedCart) {
          currentCart = await fetchCart();
          refreshCartUI();
        }
      }

      renderUI(currentCart || cart);
    } catch (err) {
      console.error('[FreeGift] Sync error:', err);
    } finally {
      isProcessing = false;
    }
  }

  /**
   * Intercept all fetch calls to /cart/ endpoints to detect cart changes.
   */
  function interceptCartRequests() {
    const originalFetch = window.fetch;

    window.fetch = function (...args) {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';

      return originalFetch.apply(this, args).then((response) => {
        // If this was a cart-modifying request, sync gifts after a brief delay
        if (
          url.includes('/cart/add') ||
          url.includes('/cart/change') ||
          url.includes('/cart/update') ||
          url.includes('/cart/clear')
        ) {
          // Don't re-trigger on our own calls
          const body = args[1]?.body;
          if (body && typeof body === 'string' && body.includes(CONFIG.giftPropertyKey)) {
            return response;
          }

          // Debounce the sync
          clearTimeout(window.__fgSyncTimeout);
          window.__fgSyncTimeout = setTimeout(syncGifts, 300);
        }
        return response;
      });
    };
  }

  /**
   * Also intercept XMLHttpRequest for themes that use it.
   */
  function interceptXHR() {
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this._fgUrl = url;
      return originalOpen.apply(this, [method, url, ...rest]);
    };

    XMLHttpRequest.prototype.send = function (body) {
      if (
        this._fgUrl &&
        (this._fgUrl.includes('/cart/add') ||
          this._fgUrl.includes('/cart/change') ||
          this._fgUrl.includes('/cart/update'))
      ) {
        if (!(body && typeof body === 'string' && body.includes(CONFIG.giftPropertyKey))) {
          this.addEventListener('load', () => {
            clearTimeout(window.__fgSyncTimeout);
            window.__fgSyncTimeout = setTimeout(syncGifts, 300);
          });
        }
      }
      return originalSend.apply(this, [body]);
    };
  }

  // ======================== LINE ITEM UI TWEAKS ========================

  /**
   * Observe the DOM for free gift line items and apply visual tweaks:
   * - Show "FREE" instead of the price
   * - Hide quantity selectors
   * - Hide remove buttons (gifts are managed automatically)
   */
  function observeCartLineItems() {
    const observer = new MutationObserver(() => {
      tweakFreeGiftLineItems();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Initial run
    tweakFreeGiftLineItems();
  }

  function tweakFreeGiftLineItems() {
    // Look for line items with the free gift property rendered in the cart
    // This depends on your theme but the property is usually rendered as a <span> or <li>
    document.querySelectorAll('[data-cart-item], .cart-item, .cart__item, cart-items .line-item').forEach((lineItem) => {
      const hasGiftProp = lineItem.innerHTML.includes(CONFIG.giftPropertyKey) ||
                          lineItem.innerHTML.includes('Free Gift') ||
                          lineItem.querySelector(`[data-free-gift="true"]`);

      if (hasGiftProp) {
        lineItem.classList.add('fg-line-item');

        // Hide the property text itself (starts with underscore — convention to hide)
        lineItem.querySelectorAll('.product-option, .cart-item__property, .properties span, dt, dd').forEach((el) => {
          if (el.textContent.includes(CONFIG.giftPropertyKey)) {
            el.style.display = 'none';
            const sibling = el.nextElementSibling || el.previousElementSibling;
            if (sibling) sibling.style.display = 'none';
          }
        });
      }
    });
  }

  // ======================== INIT ========================

  async function init() {
    try {
      await fetchFreeGiftProduct();
      await syncGifts();
      interceptCartRequests();
      interceptXHR();
      observeCartLineItems();

      // Also sync on page visibility change (e.g., user returns to tab)
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) syncGifts();
      });

      console.log('[FreeGift] Initialized successfully');
    } catch (err) {
      console.error('[FreeGift] Initialization failed:', err);
    }
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
